import React from 'react';

interface WaitingViewProps {
  roomCode: string;
  isMaster: boolean;
  playerCount?: number;
  masterName?: string;
  onStartGame: (word: string) => void;
  playerNames?: Record<string, { name: string; isOnline: boolean }>;
  masterId?: string;
  roomType?: 'friends_mode' | 'ai_player_mode';
  playerId?: string | null;
}

// Sub-component: Room Code Button
const RoomCodeButton: React.FC<{
  roomCode: string;
  copiedRoom: boolean;
  onClick: () => void;
}> = ({ roomCode, copiedRoom, onClick }) => (
  <button
    onClick={onClick}
    style={{
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 16px',
      borderRadius: '12px',
      transition: 'all 0.2s',
      background: copiedRoom ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
      transform: copiedRoom ? 'scale(0.95)' : 'scale(1)'
    }}
  >
    <h1 style={{
      fontSize: '56px',
      fontWeight: 'bold',
      color: 'white',
      margin: 0,
      letterSpacing: '2px'
    }}>
      {roomCode}
    </h1>
    <span style={{
      fontSize: '20px',
      transition: 'transform 0.3s',
      transform: copiedRoom ? 'scale(1.2)' : 'scale(1)'
    }}>
      {copiedRoom ? '✓' : '📋'}
    </span>
  </button>
);

// Sub-component: Player Count Button
const PlayerCountButton: React.FC<{
  playerCount: number;
  onClick: () => void;
}> = ({ playerCount, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      padding: '8px'
    }}
  >
    <p style={{
      fontSize: '28px',
      fontWeight: 'bold',
      color: 'white',
      margin: 0
    }}>
      {playerCount} player{playerCount !== 1 ? 's' : ''}
    </p>
    <p style={{
      fontSize: '16px',
      color: 'rgba(255, 255, 255, 0.8)',
      margin: 0
    }}>
      in the room
    </p>
  </button>
);

// Sub-component: Room Code Section (shared by both views)
const RoomCodeSection: React.FC<{
  roomCode: string;
  playerCount: number;
  copiedRoom: boolean;
  onCopyRoomCode: () => void;
  onShowPlayersPopup: () => void;
}> = ({ roomCode, playerCount, copiedRoom, onCopyRoomCode, onShowPlayersPopup }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    marginTop: '20px'
  }}>
    <p style={{
      fontSize: '16px',
      color: 'rgba(255, 255, 255, 0.9)',
      margin: 0,
      fontWeight: '500',
      letterSpacing: '1px'
    }}>
      Room Code
    </p>

    <RoomCodeButton
      roomCode={roomCode}
      copiedRoom={copiedRoom}
      onClick={onCopyRoomCode}
    />

    <PlayerCountButton
      playerCount={playerCount}
      onClick={onShowPlayersPopup}
    />
  </div>
);

// Sub-component: Players Popup (shared by both views)
const PlayersPopup: React.FC<{
  isOpen: boolean;
  playerNames: Record<string, { name: string; isOnline: boolean }>;
  masterId?: string;
  currentPlayerId?: string | null;
  onClose: () => void;
}> = ({ isOpen, playerNames, masterId, currentPlayerId, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '20px',
          padding: '24px',
          maxWidth: '400px',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
        }}
      >
        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#333',
          margin: '0 0 20px 0',
          textAlign: 'center'
        }}>
          Players
        </h2>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {Object.entries(playerNames).map(([playerId, playerData]) => (
            <div
              key={playerId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                background: playerId === masterId ? 'rgba(192, 132, 252, 0.15)' : '#f9fafb',
                borderRadius: '12px',
                border: playerId === masterId ? '2px solid #a855f7' : '1px solid #e5e7eb',
                opacity: playerData.isOnline ? 1 : 0.6
              }}
            >
              <span style={{
                fontSize: '20px'
              }}>
                {playerId === masterId ? '👑' : '👤'}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    fontSize: '16px',
                    fontWeight: playerId === masterId ? '600' : '500',
                    color: playerId === masterId ? '#a855f7' : '#374151'
                  }}>
                    {playerData.name}{playerId === currentPlayerId && ' (you)'}
                  </span>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: playerData.isOnline ? '#10b981' : '#d1d5db'
                  }} />
                </div>
              </div>
              {playerId === masterId && (
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#a855f7',
                  background: 'rgba(168, 85, 247, 0.1)',
                  padding: '4px 8px',
                  borderRadius: '6px'
                }}>
                  Master
                </span>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            marginTop: '20px',
            padding: '12px',
            background: '#6366f1',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export const WaitingView: React.FC<WaitingViewProps> = ({ 
  roomCode, 
  isMaster, 
  playerCount = 1,
  masterName = 'Master',
  onStartGame,
  playerNames = {},
  masterId,
  roomType = 'friends_mode',
  playerId
}) => {
  const resolvedMasterName = masterId && playerNames[masterId] ? playerNames[masterId].name : masterName;
  const [secretWord, setSecretWord] = React.useState('');
  const [showCapitalWarning, setShowCapitalWarning] = React.useState(false);
  const [showPlayerCountWarning, setShowPlayerCountWarning] = React.useState(false);
  const warningTimeoutRef = React.useRef<number | null>(null);
  const playerCountWarningTimeoutRef = React.useRef<number | null>(null);
  const [showPlayersPopup, setShowPlayersPopup] = React.useState(false);
  const [copiedRoom, setCopiedRoom] = React.useState(false);

  React.useEffect(() => () => {
    if (warningTimeoutRef.current !== null) {
      window.clearTimeout(warningTimeoutRef.current);
    }
    if (playerCountWarningTimeoutRef.current !== null) {
      window.clearTimeout(playerCountWarningTimeoutRef.current);
    }
  }, []);

  const isSecretWordValid = (word: string) => {
    const trimmed = word.trim();
    return trimmed.length >= 2 && trimmed.length <= 20;
  };

  const getMinPlayerCount = () => {
    return roomType === 'ai_player_mode' ? 2 : 3;
  };

  const minPlayerCount = getMinPlayerCount();

  const handleStartGame = () => {
    if (playerCount < minPlayerCount) {
      setShowPlayerCountWarning(true);
      if (playerCountWarningTimeoutRef.current !== null) {
        window.clearTimeout(playerCountWarningTimeoutRef.current);
      }
      playerCountWarningTimeoutRef.current = window.setTimeout(() => {
        setShowPlayerCountWarning(false);
      }, 2000);
      return;
    }
    if (isSecretWordValid(secretWord)) {
      onStartGame(secretWord);
      setSecretWord('');
    }
  };

  const copyRoomCode = () => {
    navigator.clipboard.writeText(roomCode);
    setCopiedRoom(true);
    setTimeout(() => setCopiedRoom(false), 2000);
  };

  if (!isMaster) {
    // Player view
    return (
      <div style={{
        width: '100%',
        height: '100vh',
        background: 'linear-gradient(180deg, #C084FC 0%, #EC4899 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 20px',
        position: 'relative',
        overflow: 'auto'
      }}>
        {/* Room Code Section - Shared Component */}
        <RoomCodeSection
          roomCode={roomCode}
          playerCount={playerCount}
          copiedRoom={copiedRoom}
          onCopyRoomCode={copyRoomCode}
          onShowPlayersPopup={() => setShowPlayersPopup(true)}
        />

        {/* Waiting animation */}
        <div style={{
          margin: '60px 0 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px'
        }}>
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            border: '4px solid rgba(255, 255, 255, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '48px',
            animation: 'pulse 2s ease-in-out infinite'
          }}>
            ⏳
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: 'white',
              margin: 0
            }}>
              Waiting for Master
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'rgba(255, 255, 255, 0.9)',
              margin: 0,
              textAlign: 'center'
            }}>
              {resolvedMasterName} (master) is choosing a secret word...
            </p>
          </div>
        </div>

        {/* Role badge */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '24px',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '32px'
        }}>
          <span style={{ fontSize: '20px' }}>👤</span>
          <span style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'white'
          }}>
            You are a Guesser
          </span>
        </div>

        {/* Instructions */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          width: '100%',
          maxWidth: '400px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>💡</span>
            <p style={{
              fontSize: '16px',
              color: 'white',
              margin: 0
            }}>
              Give hints to help others
            </p>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>✋</span>
            <p style={{
              fontSize: '16px',
              color: 'white',
              margin: 0
            }}>
              Contact when you understand a hint
            </p>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '24px' }}>🔒</span>
            <p style={{
              fontSize: '16px',
              color: 'white',
              margin: 0
            }}>
              Guess the secret word!
            </p>
          </div>
        </div>

        {/* Players Popup - Shared Component */}
        <PlayersPopup
          isOpen={showPlayersPopup}
          playerNames={playerNames}
          masterId={masterId}
          currentPlayerId={playerId}
          onClose={() => setShowPlayersPopup(false)}
        />

        <style>
          {`
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                opacity: 1;
              }
              50% {
                transform: scale(1.1);
                opacity: 0.8;
              }
            }
          `}
        </style>
      </div>
    );
  }

  // Master view
  return (
    <div style={{
      width: '100%',
      height: '100vh',
      background: 'linear-gradient(180deg, #C084FC 0%, #EC4899 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px 20px',
      position: 'relative',
      justifyContent: 'flex-start',
      overflow: 'auto'
    }}>
      {/* Room Code Section - Shared Component */}
      <RoomCodeSection
        roomCode={roomCode}
        playerCount={playerCount}
        copiedRoom={copiedRoom}
        onCopyRoomCode={copyRoomCode}
        onShowPlayersPopup={() => setShowPlayersPopup(true)}
      />

      {/* Spacer */}
      <div style={{ flex: 1, minHeight: '20px' }} />

      {/* Master badge */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '24px',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '0px',
        marginBottom: '20px'
      }}>
        <span style={{ fontSize: '24px' }}>👑</span>
        <span style={{
          fontSize: '18px',
          fontWeight: '600',
          color: 'white'
        }}>
          You are the Master
        </span>
      </div>

      {/* Secret word section */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        width: '100%',
        maxWidth: '400px',
        marginBottom: '20px'
      }}>
        <h2 style={{
          fontSize: '32px',
          fontWeight: 'bold',
          color: 'white',
          margin: 0,
          textAlign: 'center'
        }}>
          Choose a Secret Word
        </h2>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          marginBottom: '4px'
        }}>
          <label style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'white'
          }}>
            Secret Word
          </label>
          <span style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.7)'
          }}>
            {secretWord.length}/20
          </span>
        </div>

        <input
          type="text"
          placeholder="Enter secret word"
          value={secretWord}
          onChange={(e) => {
            const rawValue = e.target.value;
            if (rawValue.length > 20) return;
            
            const uppercase = rawValue.toUpperCase();
            const sanitized = uppercase.replace(/[^A-Z]/g, '');
            const hasInvalidChars = /[^a-zA-Z]/.test(rawValue);

            setSecretWord(sanitized);

            if (hasInvalidChars) {
              setShowCapitalWarning(true);
              if (warningTimeoutRef.current !== null) {
                window.clearTimeout(warningTimeoutRef.current);
              }
              warningTimeoutRef.current = window.setTimeout(() => {
                setShowCapitalWarning(false);
              }, 500);
            } else {
              setShowCapitalWarning(false);
            }
          }}
          maxLength={20}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '18px',
            borderRadius: '12px',
            border: 'none',
            background: 'white',
            color: '#333',
            boxSizing: 'border-box'
          }}
        />

        {showCapitalWarning && (
          <div style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '10px',
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            Only capital letters (A-Z) are allowed.
          </div>
        )}

        {showPlayerCountWarning && (
          <div style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: '10px',
            background: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            Requires {minPlayerCount} players to start the game.
          </div>
        )}


        <button
          onClick={handleStartGame}
          disabled={!isSecretWordValid(secretWord) || playerCount < minPlayerCount}
          style={{
            width: '100%',
            padding: '16px',
            fontSize: '18px',
            fontWeight: 'bold',
            borderRadius: '12px',
            border: 'none',
            background: (isSecretWordValid(secretWord) && playerCount >= minPlayerCount) ? 'rgba(255, 255, 255, 0.3)' : 'rgba(128, 128, 128, 0.3)',
            color: 'white',
            cursor: (isSecretWordValid(secretWord) && playerCount >= minPlayerCount) ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
        >
          <span style={{ fontSize: '20px' }}>▶</span>
          Start Game
        </button>
      </div>

      {/* Players Popup - Shared Component */}
      <PlayersPopup
        isOpen={showPlayersPopup}
        playerNames={playerNames}
        masterId={masterId}
        currentPlayerId={playerId}
        onClose={() => setShowPlayersPopup(false)}
      />

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.8;
            }
          }
        `}
      </style>
    </div>
  );
};
